import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { openCreateGifteeWizard } from "../createGifteeWizardNav.js";
import { useGiftees } from "../hooks/useGiftees";
import { useSavedRecommendations } from "../hooks/useSavedRecommendations";
import { useAllReminders } from "../hooks/useAllReminders";
import { getBudgetMidpointDollars } from "../utils/formatBudget";
import styles from "../styles/GifteeAllGifteesScreen.module.css";
import GifteeAppBottomNav from "../components/GifteeAppBottomNav.jsx";

function AllGifteeCard({ giftee }) {
  const navigate = useNavigate();
  const { savedRecommendations, loading } = useSavedRecommendations(giftee.id);
  const { allReminders, loading: remindersLoading } = useAllReminders([giftee]);
  
  const budgetMin = giftee.budgetMin || 0;
  const budgetMax = giftee.budgetMax || budgetMin + 50;
  const celebrationCount = remindersLoading ? 0 : allReminders.filter(r => r.gifteeId === giftee.id).length;
  const recCount = loading
    ? 0
    : savedRecommendations.length > 0
      ? savedRecommendations.length
      : 2;

  return (
    <button
      type="button"
      className={styles.gifteeCard}
      onClick={() => navigate(`/giftee/${giftee.id}`)}
      aria-label={`View details for ${giftee.name || "giftee"}`}
    >
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <h3 className={styles.gifteeName}>{giftee.name || "Giftee"}</h3>
          <div className={styles.arrowIcon}>
            <i className="fa-solid fa-arrow-right" />
          </div>
        </div>
        <div className={styles.tagRow}>
          <div className={styles.tagCelebrations}>
            {celebrationCount} Celebration{celebrationCount !== 1 ? 's' : ''}
          </div>
          <div className={styles.tagRecommendations}>
            {recCount} Recommendation{recCount !== 1 ? 's' : ''}
          </div>
          <div className={styles.tagBudget}>
            Budget: ${budgetMin} - ${budgetMax}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function GifteeAllGifteesScreen() {
  const navigate = useNavigate();
  const { giftees, loading } = useGiftees();

  useEffect(() => {
    if (loading) return;
    if (giftees.length === 0) {
      openCreateGifteeWizard(navigate, "/all-giftees", { replace: true });
    }
  }, [loading, giftees.length, navigate]);

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (!giftees.length) {
    return null;
  }

  return (
    <div className={styles.shell}>
      {/* Main Content - single scrollable area; header is first child so sticky works */}
      <main className={styles.content}>
        {/* Header - sticky within the scroll container so backdrop-filter works */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>All Giftees</h1>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => navigate(-1)}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={styles.titleSection}>
          <div className={styles.gifteeCount}>
            <div className={styles.countBox}>
              {giftees.length}
            </div>
            <div className={styles.countLabel}>Giftees</div>
          </div>
          <button
            type="button"
            className={styles.addNewBtn}
            onClick={() => openCreateGifteeWizard(navigate, "/all-giftees")}
          >
            Add New
            <i className="fa-solid fa-plus" />
          </button>
        </div>

        <div className={styles.gifteesList}>
          {giftees.map((giftee) => (
            <AllGifteeCard key={giftee.id} giftee={giftee} />
          ))}
        </div>
      </main>
      <GifteeAppBottomNav />
    </div>
  );
}
